import type { CSSProperties } from "react";
import type { Edge, EdgeMarker } from "@xyflow/react";
import {
    IVR_TREE_BUS_TO_CHILD_DROP_PX,
    IVR_EDGE_LOCAL_HORIZONTAL_SPAN_PX,
    IVR_EDGE_CORRIDOR_BELOW_SOURCE_PX,
} from "./ivrSchemeConstants";

export type IvrEdgeVisualKind = "chain" | "jump" | "default";

const START_CONFIG_PREFIX = "start-config-";
const CHAIN_PREFIX = "start-chain-";

type IvrEdgeNodeRef = {
    id: string;
    data?: {
        configParentId?: string;
        isGenerated?: boolean;
    };
};

export function ivrIsIntraChainStructuralEdge(
    sourceId: string,
    targetId: string,
    nodes: IvrEdgeNodeRef[]
): boolean {
    const src = nodes.find((n) => n.id === sourceId);
    const tgt = nodes.find((n) => n.id === targetId);
    if (!src || !tgt) return false;

    if (
        sourceId.startsWith(START_CONFIG_PREFIX) &&
        targetId.startsWith(CHAIN_PREFIX)
    ) {
        return tgt.data?.configParentId === sourceId;
    }

    if (
        sourceId.startsWith(CHAIN_PREFIX) &&
        targetId.startsWith(CHAIN_PREFIX)
    ) {
        const srcParent = src.data?.configParentId;
        const tgtParent = tgt.data?.configParentId;
        return (
            !!srcParent &&
            srcParent === tgtParent &&
            !!src.data?.isGenerated &&
            !!tgt.data?.isGenerated
        );
    }

    if (
        sourceId === "ivr-base" &&
        targetId.startsWith(START_CONFIG_PREFIX)
    ) {
        return true;
    }

    return false;
}

export function ivrResolveEdgeVisualKind(
    edge: Pick<Edge, "source" | "target" | "data">,
    nodes: IvrEdgeNodeRef[],
    isConfigurableLink: (
        e: Pick<Edge, "source" | "target" | "data">
    ) => boolean
): IvrEdgeVisualKind {
    if (isConfigurableLink(edge)) return "jump";
    if (ivrIsIntraChainStructuralEdge(edge.source, edge.target, nodes)) {
        return "chain";
    }
    return "default";
}

function ivrBuildLocalBusDropPath(
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    busDropPx: number = IVR_TREE_BUS_TO_CHILD_DROP_PX
): { path: string; labelX: number; labelY: number } {
    if (Math.abs(sourceX - targetX) < 1.5) {
        return {
            path: `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`,
            labelX: sourceX,
            labelY: (sourceY + targetY) / 2,
        };
    }
    const busY = targetY - busDropPx;
    return {
        path: `M ${sourceX} ${sourceY} L ${sourceX} ${busY} L ${targetX} ${busY} L ${targetX} ${targetY}`,
        labelX: (sourceX + targetX) / 2,
        labelY: (busY + targetY) / 2,
    };
}

function ivrBuildCorridorEdgePath(
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number
): { path: string; labelX: number; labelY: number } {
    if (Math.abs(sourceX - targetX) < 1.5) {
        return {
            path: `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`,
            labelX: sourceX,
            labelY: (sourceY + targetY) / 2,
        };
    }

    const dy = targetY - sourceY;
    const minCorridorY = sourceY + IVR_EDGE_CORRIDOR_BELOW_SOURCE_PX;
    const maxCorridorY = targetY - IVR_TREE_BUS_TO_CHILD_DROP_PX - 8;
    let corridorY =
        dy > 96
            ? sourceY + dy * 0.4
            : sourceY + IVR_EDGE_CORRIDOR_BELOW_SOURCE_PX;
    corridorY = Math.max(minCorridorY, Math.min(maxCorridorY, corridorY));

    return {
        path: `M ${sourceX} ${sourceY} L ${sourceX} ${corridorY} L ${targetX} ${corridorY} L ${targetX} ${targetY}`,
        labelX: (sourceX + targetX) / 2,
        labelY: corridorY,
    };
}

export function ivrBuildIvrSchemeEdgePath(
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    options?: { preferLocalBus?: boolean }
): { path: string; labelX: number; labelY: number } {
    const dx = Math.abs(sourceX - targetX);
    if (options?.preferLocalBus || dx <= IVR_EDGE_LOCAL_HORIZONTAL_SPAN_PX) {
        return ivrBuildLocalBusDropPath(sourceX, sourceY, targetX, targetY);
    }
    return ivrBuildCorridorEdgePath(sourceX, sourceY, targetX, targetY);
}

export function ivrEdgeHighlightOpacity(
    edgeId: string,
    sourceId: string,
    targetId: string,
    selectedNodeIds: ReadonlySet<string>,
    selectedEdgeIds: ReadonlySet<string>
): number {
    if (selectedNodeIds.size === 0 && selectedEdgeIds.size === 0) {
        return 1;
    }
    if (selectedEdgeIds.has(edgeId)) return 1;
    if (selectedNodeIds.has(sourceId) || selectedNodeIds.has(targetId)) {
        return 1;
    }
    return 0.16;
}

export const IVR_LIGHT_SOLID_EDGE_STROKE = "#6b7b90";
export const IVR_LIGHT_DASHED_EDGE_STROKE = "#556275";

export function ivrResolveLightSchemeMarkerEnd(
    markerEnd: EdgeMarker | string | undefined,
    visualKind: IvrEdgeVisualKind,
    selected: boolean
): EdgeMarker | string | undefined {
    if (!markerEnd || typeof markerEnd === "string") return markerEnd;
    if (selected) {
        return { ...markerEnd, color: "rgb(220 38 38)" };
    }
    const color =
        visualKind === "jump"
            ? IVR_LIGHT_DASHED_EDGE_STROKE
            : IVR_LIGHT_SOLID_EDGE_STROKE;
    return { ...markerEnd, color };
}

export function ivrEdgeVisualStyle(
    visualKind: IvrEdgeVisualKind,
    highlightOpacity: number,
    selected: boolean,
    isLightScheme = false
): CSSProperties {
    if (selected) {
        return { stroke: "rgb(220 38 38)", opacity: 1 };
    }

    const base: CSSProperties = { opacity: highlightOpacity };

    if (visualKind === "chain") {
        if (isLightScheme) {
            return {
                ...base,
                stroke: IVR_LIGHT_SOLID_EDGE_STROKE,
                strokeWidth: 1.9,
                opacity: highlightOpacity * 0.96,
            };
        }
        return {
            ...base,
            strokeWidth: 1,
            opacity: highlightOpacity * 0.72,
        };
    }

    if (visualKind === "jump") {
        if (isLightScheme) {
            return {
                ...base,
                stroke: IVR_LIGHT_DASHED_EDGE_STROKE,
                strokeWidth: 1.7,
                strokeDasharray: "7 5",
            };
        }
        return {
            ...base,
            strokeWidth: 1.35,
            strokeDasharray: "7 5",
            stroke: "#6d7a96",
        };
    }

    if (isLightScheme) {
        return {
            ...base,
            stroke: IVR_LIGHT_SOLID_EDGE_STROKE,
            strokeWidth: 1.7,
        };
    }

    return {
        ...base,
        strokeWidth: 1.15,
    };
}
